import { SaturdayClubTermCalendar, ClubRegistrationLookup, SaturdayClubTermPricing, SaturdayClubSessionInfo, SaturdayClubClosedNotice, ClubPaymentSection, fadeInUp } from "./LuxurySaturdayClubSections";
import { stableRecordKey } from '../lib/rowIdentity';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Calendar, Heart, Star, Megaphone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MandalaCorner } from '../components/luxury/MandalaCorner';
import { PatolaBorder } from '../components/luxury/PatolaBorder';
import { toast } from 'sonner';
import { usePageContent, Paragraphs, PreviewBanner } from '../lib/content';
const OBJECTIVES = [{
  icon: Users,
  title: 'Social Interaction',
  desc: 'Build friendships and connections within the community'
}, {
  icon: Heart,
  title: 'Cultural Activities',
  desc: 'Engage in traditional Gujarati cultural programs'
}, {
  icon: Star,
  title: 'Learning',
  desc: 'Educational activities and skill development workshops'
}, {
  icon: Calendar,
  title: 'Regular Meetups',
  desc: 'Consistent Saturday gatherings for community bonding'
}];
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
export default function LuxurySaturdayClub() {
  const {
    t,
    c,
    isPreview
  } = usePageContent('saturday_club');
  const objectives = Array.isArray(c.objectives) && c.objectives.length ? c.objectives.map((o, i) => ({
    ...o,
    icon: OBJECTIVES[i % OBJECTIVES.length].icon
  })) : OBJECTIVES;
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    child_name: '',
    child_age: '',
    address: '',
    postcode: ''
  });
  const [regFiles, setRegFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  // Payment step (shown after a registration is created, or when returning with ?registration_id=)
  const [registration, setRegistration] = useState(null);
  const [payOption, setPayOption] = useState('term');
  const [customAmount, setCustomAmount] = useState('');
  const [paying, setPaying] = useState(false);
  const [pricing, setPricing] = useState({
    term: 51,
    yearly: 150
  });
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);
  const [info, setInfo] = useState(null); // admin-configured club info (terms, ages, notices, contact)
  const [termId, setTermId] = useState('');
  // "Pay fees" for returning families: find an existing registration by parent email + child's name
  const [findOpen, setFindOpen] = useState(false);
  const [findForm, setFindForm] = useState({
    email: '',
    child_name: ''
  });
  const [finding, setFinding] = useState(false);
  const [found, setFound] = useState([]);
  const [payAgain, setPayAgain] = useState(false); // show fee options again on an already-paid registration

  useEffect(() => {
    fetch(`${BACKEND}/api/saturday-club/pricing`).then(r => r.json()).then(d => {
      if (d.pricing) setPricing(d.pricing);
      if (typeof d.payments_enabled === 'boolean') setPaymentsEnabled(d.payments_enabled);
    }).catch(() => {});
    fetch(`${BACKEND}/api/saturday-club/info`).then(r => r.json()).then(d => {
      setInfo(d);
      if (d.prices) setPricing(d.prices);
      const first = (d.payable_terms || [])[0];
      if (first) setTermId(first.term_id);
    }).catch(() => setInfo({
      registrations_open: true,
      terms: [],
      payable_terms: [],
      show_terms: true,
      allow_custom: true
    }));
  }, []);
  const fmtDay = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }) : '';
  const payableTerms = info?.payable_terms || [];
  const allowCustom = info ? info.allow_custom !== false : true;
  useEffect(() => {
    const rid = searchParams.get('registration_id');
    if (!rid) return;
    fetch(`${BACKEND}/api/saturday-club/registration/${encodeURIComponent(rid)}`).then(async r => {
      if (!r.ok) throw new Error('not found');
      const d = await r.json();
      setRegistration(d);
      if (searchParams.get('payment') === 'cancelled') toast.info('Payment was cancelled — you can pay whenever you are ready.');
    }).catch(() => toast.error('We could not find that registration reference - please check it, or contact us and we will gladly help.'));
  }, [searchParams]);
  const handlePay = async () => {
    if (!registration) return;
    if (payOption === 'custom' && (!customAmount || Number(customAmount) < 0.5)) {
      toast.error('Please enter an amount of at least £0.50');
      return;
    }
    setPaying(true);
    try {
      const res = await fetch(`${BACKEND}/api/saturday-club/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          registration_id: registration.registration_id,
          payment_option: payOption,
          custom_amount: payOption === 'custom' ? Number(customAmount) : null,
          term_id: payOption === 'term' && termId ? termId : null,
          origin_url: window.location.origin
        })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not start payment');
      if (d.url) window.location.href = d.url;else throw new Error('Could not start payment');
    } catch (err) {
      toast.error(err.message || 'Could not start payment');
      setPaying(false);
    }
  };
  const payAmount = payOption === 'custom' ? Number(customAmount || 0) : Number(pricing[payOption] || 0);
  const scrollToPayment = () => setTimeout(() => document.getElementById('saturday-club-payment')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  }), 150);
  const scrollToRegister = () => document.getElementById('saturday-club-register')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
  const chooseRegistration = reg => {
    setRegistration(reg);
    setPayAgain(reg.payment_status === 'paid');
    setFindOpen(false);
    setFound([]);
    toast.success(`Found ${reg.child_name}'s registration — choose a fee option below.`);
    scrollToPayment();
  };
  const handleFind = async e => {
    e?.preventDefault?.();
    if (!findForm.email.trim() || !findForm.child_name.trim()) {
      toast.error("Please enter the parent email and the child's name");
      return;
    }
    setFinding(true);
    try {
      const res = await fetch(`${BACKEND}/api/saturday-club/find-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: findForm.email.trim(),
          child_name: findForm.child_name.trim()
        })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Registration not found');
      const regs = d.registrations || [];
      if (regs.length === 1) chooseRegistration(regs[0]);else setFound(regs);
    } catch (err) {
      toast.error(err.message || 'Registration not found');
    } finally {
      setFinding(false);
    }
  };
  const handleDownloadForm = () => {
    // Generate a simple registration form template
    const formContent = `SATURDAY CLUB REGISTRATION FORM
Gujarat Samaj Nottingham

Parent/Guardian Information:
Name: _______________________________
Email: _______________________________
Phone: _______________________________

Child Information:
Name: _______________________________
Age: _______________________________

Please complete this form and upload it back to our website or email it to info@gujaratsamajnottingham.com

For more information, please contact us.`;
    const blob = new Blob([formContent], {
      type: 'text/plain'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Saturday_Club_Registration_Form.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('The registration form has been downloaded - thank you');
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(form).forEach(key => formData.append(key, form[key]));
      if (regFiles.length) {
        regFiles.forEach(f => formData.append('documents', f));
      }
      const res = await fetch(`${BACKEND}/api/saturday-club/register`, {
        method: 'POST',
        body: formData
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({
          detail: 'Registration failed'
        }));
        throw new Error(err.detail || 'Registration failed');
      }
      const created = await res.json();
      toast.success('Registration received! Choose how you would like to pay below.');
      setForm({
        name: '',
        email: '',
        phone: '',
        child_name: '',
        child_age: '',
        address: '',
        postcode: ''
      });
      setRegFiles([]);
      setRegistration({
        registration_id: created.registration_id,
        child_name: created.child_name,
        parent_first_name: (created.name || '').split(' ')[0],
        payment_status: 'unpaid',
        pricing: created.pricing
      });
      setSearchParams({
        registration_id: created.registration_id
      });
      setTimeout(() => document.getElementById('saturday-club-payment')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      }), 150);
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };
  return <div className="overflow-hidden">
      {isPreview && <PreviewBanner page="Saturday Club" />}
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-right" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }}>
            <Badge className="mb-6 bg-[hsl(var(--lux-gold-soft))] text-[hsl(var(--lux-royal))] border-[hsl(var(--lux-gold)/0.4)] text-base px-4 py-2">
              <Users className="w-4 h-4 mr-2" /> {t('hero_badge', 'Community Program')}
            </Badge>
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{
            fontFamily: 'Cormorant Garamond'
          }}>
              {t('hero_title', 'Saturday')} <span className="text-[hsl(var(--lux-gold))]">{t('hero_title_accent', 'Club')}</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto mb-8">
              {t('hero_subtitle', 'A vibrant community gathering every Saturday for cultural activities, social interaction, and learning.')}
            </p>
            <div className="lux-gold-divider mx-auto max-w-md" />
          </motion.div>
        </div>
      </section>

      {info?.notice && <section className="bg-[hsl(var(--lux-gold-soft))] border-y border-[hsl(var(--lux-gold)/0.4)]" data-testid="saturday-club-notice">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-start gap-3 text-[hsl(var(--lux-royal))]">
            <Megaphone className="w-5 h-5 mt-0.5 shrink-0" />
            <p className="text-sm sm:text-base font-medium">{info.notice}</p>
          </div>
        </section>}

      {/* About Section */}
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card">
              <PatolaBorder />
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{
                fontFamily: 'Cormorant Garamond'
              }}>
                  {t('about_heading', 'About Saturday Club')}
                </h2>
                <div className="prose prose-lg max-w-none text-[hsl(var(--lux-ink)/0.8)] space-y-4">
                  {c.about_text ? <Paragraphs text={c.about_text} /> : <>
                  <p>
                    The Saturday Club is a cherished community program that brings together members of Gujarat Samaj Nottingham for weekly gatherings filled with culture, learning, and friendship.
                  </p>
                  <p>
                    Every Saturday, community members come together to participate in traditional activities, share experiences, and strengthen the bonds that make our community special. From cultural performances to educational workshops, the Saturday Club offers something for everyone.
                  </p>
                  <p>
                    Whether you're new to the area or a long-time member, Saturday Club provides a welcoming space to connect with fellow community members and celebrate our shared Gujarati heritage.
                  </p>
                </>}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Objectives */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fadeInUp} className="text-3xl font-bold text-center text-[hsl(var(--lux-royal))] mb-12" style={{
          fontFamily: 'Cormorant Garamond'
        }}>
            {t('objectives_heading', 'Our Objectives')}
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {objectives.map((obj, idx) => <motion.div key={stableRecordKey(obj)} {...fadeInUp} transition={{
            delay: idx * 0.1
          }}>
                <Card className="lux-ornate-card h-full hover:shadow-[var(--lux-shadow-2)] transition-all text-center">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center">
                      <obj.icon className="w-8 h-8 text-[hsl(var(--lux-royal))]" />
                    </div>
                    <h3 className="text-lg font-bold text-[hsl(var(--lux-royal))] mb-2" style={{
                  fontFamily: 'Cormorant Garamond'
                }}>
                      {obj.title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--lux-ink)/0.7)]">{obj.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <SaturdayClubTermPricing paymentsEnabled={paymentsEnabled} registration={registration} scrollToPayment={scrollToPayment} scrollToRegister={scrollToRegister} setFindOpen={setFindOpen} setPayAgain={setPayAgain} t={t} />

      {/* Term dates (admin-configured; nothing invented) */}
      {info?.show_terms !== false && <SaturdayClubTermCalendar fmtDay={fmtDay} info={info} />}

      {/* Registration Section */}
      <SaturdayClubClosedNotice form={form} handleDownloadForm={handleDownloadForm} handleSubmit={handleSubmit} info={info} loading={loading} regFiles={regFiles} setForm={setForm} setRegFiles={setRegFiles} t={t} />

      {/* Payment step */}
      {registration && <ClubPaymentSection allowCustom={allowCustom} customAmount={customAmount} fmtDay={fmtDay} handlePay={handlePay} payAgain={payAgain} payAmount={payAmount} payOption={payOption} payableTerms={payableTerms} paying={paying} paymentsEnabled={paymentsEnabled} pricing={pricing} registration={registration} setCustomAmount={setCustomAmount} setPayAgain={setPayAgain} setPayOption={setPayOption} setTermId={setTermId} t={t} termId={termId} />}

      {/* Returning families: find registration to pay */}
      <ClubRegistrationLookup chooseRegistration={chooseRegistration} findForm={findForm} findOpen={findOpen} finding={finding} found={found} handleFind={handleFind} scrollToRegister={scrollToRegister} setFindForm={setFindForm} setFindOpen={setFindOpen} setFound={setFound} />

      {/* Meeting Info */}
      <SaturdayClubSessionInfo info={info} t={t} />
    </div>;
}
